import { ToggleGroup, Options } from '@instructure/ui';
import InfoIcon from '@ui/shared/components/Common/InfoIcon';
import {
  Badge,
  Tooltip,
  Flex,
  View,
  Text,
} from '@smarterservices/smarter-ui';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';

/**
 * SentBack Component
 * 
 * Displays a collapsible list of sessions that have been sent back to onboarding.
 * Shows the student's name and displays the proctor who sent them back in a tooltip.
 * 
 * @param {Object} props - Component props
 * @param {boolean} [props.expanded=true] - Whether the toggle group is expanded by default
 * @returns {JSX.Element} The sent back sessions component
 */
const SentBack = ({ expanded = true }) => {
  const sentback = useSessionStore((state) => state.sentback);
  const proctors = useAppStore((state) => state.proctors);

  /**
   * Gets the name of the proctor who sent the session back
   * @param {string} sentBackBy - The proctor's identifier
   * @returns {string} The proctor's full name or 'Unknown'
   */
  const getProctorName = (sentBackBy) => {
    if (!sentBackBy || !proctors[sentBackBy]) {
      return 'Unknown';
    }
    const proctor = proctors[sentBackBy];
    return `${proctor.firstName} ${proctor.lastName}`;
  };

  const sentBackCount = Object.keys(sentback).length;

  return (
    <ToggleGroup
      toggleLabel="Sessions that were sent back to onboarding"
      padding="0 0 xxx-small 0"
      defaultExpanded={expanded}
      summary={
        <Flex justifyItems="space-between" margin="none small none none">
          <Flex.Item>Sent Back <InfoIcon margin="0 0 xxx-small xx-small" color="secondary" tip="This includes sessions that were sent back to an onboarding step by a proctor. These sessions will eventually end up back in check-in or will remain connected to the proctor who sent them back." size="xx-small" /></Flex.Item>
          <Flex.Item>
            <Badge
              count={sentBackCount}
              variant="primary"
              standalone
            />
          </Flex.Item>
        </Flex>
      }
    >
      {sentBackCount > 0 ? (
        <Options>
          {Object.entries(sentback).map(([sessionSid, data]) => (
            <Options.Item key={`session_sentback_${sessionSid}`}>
              <Flex alignItems="center">
                {/* Student name with sent-back proctor tooltip */}
                <Tooltip
                  color="primary-inverse"
                  renderTip={getProctorName(data?.sentBackBy)}
                  placement="end"
                >
                  <Flex.Item grow>
                    {data?.firstName} {data?.lastName}
                  </Flex.Item>
                </Tooltip>
              </Flex>
            </Options.Item>
          ))}
        </Options>
      ) : (
        <View as="div" padding="small" textAlign="center">
          <Text>No sessions have been sent back to onboarding</Text>
        </View>
      )}
    </ToggleGroup>
  );
};

export default SentBack;
